"use client";

import { c as _c } from "react/compiler-runtime";
import { IconMagnifyingGlass } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { GridBorderedSection, GridDivider } from "@/components/homepage/GridLayout";
import { HomepageFooter } from "@/components/homepage/HomepageFooter";
import { Input } from "@/components/ui/input";
import { PageLayout } from "@/components/ui/page-layout";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useApps, type App } from "@/hooks/useApps";
import type { ServerApp } from "@/lib/api/server-fetchers";
import { getCategoryForTag, TAG_CATEGORY_LABELS } from "@/lib/tag-categories";
import { useIsAuthenticated } from "@/hooks/useIsAuthenticated";
import { PixelLoader } from "@/components/ui/pixel-loader";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { EASE_OUT_QUART } from "@/lib/animation-constants";
import { motion } from "motion/react";
import { Suspense, useMemo, useState } from "react";
import { IntegrationHero } from "./IntegrationHero";
import { FilterSidebar } from "@/components/ui/filter-sidebar";
import { useX402Endpoints } from "@/hooks/useX402Endpoints";
import { PublicAppCard } from "./PublicAppCard";
const ALL_CATEGORIES = "All Categories";
function getAppCategories(app: App | ServerApp): Set<string> {
  const cats = new Set<string>();
  for (const tag of app.tags ?? []) {
    cats.add(getCategoryForTag(tag));
  }
  for (const cat of app.categories ?? []) {
    cats.add(cat);
  }
  cats.delete("Other");
  return cats;
}
function IntegrationsPageContent({
  initialApps,
  x402EndpointCount
}: {
  initialApps?: ServerApp[];
  x402EndpointCount?: number;
}) {
  const {
    apps: clientApps,
    isLoading: clientLoading
  } = useApps();
  const {
    endpoints: x402Endpoints
  } = useX402Endpoints();
  const x402Count = x402EndpointCount ?? x402Endpoints.length;
  const isAuthenticated = useIsAuthenticated();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState(() => searchParams.get("category") || ALL_CATEGORIES);
  const [searchQuery, setSearchQuery] = useState("");
  const apps = initialApps ? initialApps as App[] : clientApps;
  const isLoading = !initialApps && clientLoading;
  const visibleApps = useMemo(() => {
    return apps.filter(app => !app.isSpecialCategory).map(app_0 => ({
      app: app_0,
      categories: getAppCategories(app_0)
    }));
  }, [apps]);
  const sidebarCategories = useMemo(() => {
    const populated = new Set<string>();
    for (const {
      categories
    } of visibleApps) {
      for (const cat of categories) {
        populated.add(cat);
      }
    }
    const ordered: string[] = [ALL_CATEGORIES];
    for (const label of TAG_CATEGORY_LABELS) {
      if (populated.has(label)) ordered.push(label);
    }
    for (const cat_0 of [...populated].sort()) {
      if (!ordered.includes(cat_0)) ordered.push(cat_0);
    }
    return ordered;
  }, [visibleApps]);
  const activeCategory = sidebarCategories.includes(selectedCategory) ? selectedCategory : ALL_CATEGORIES;
  const filteredApps = useMemo(() => {
    let result = visibleApps;
    if (activeCategory !== ALL_CATEGORIES) {
      result = result.filter(({
        categories: categories_0
      }) => categories_0.has(activeCategory));
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(({
        app: app_1
      }) => app_1.name.toLowerCase().includes(query) || app_1.description?.toLowerCase().includes(query));
    }
    return result.sort((a, b) => b.app.totalIntegrations - a.app.totalIntegrations);
  }, [visibleApps, searchQuery, activeCategory]);
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    const params = new URLSearchParams(searchParams.toString());
    if (category === ALL_CATEGORIES) {
      params.delete("category");
    } else {
      params.set("category", category);
    }
    const queryString = params.toString();
    router.replace(queryString ? `${pathname}?${queryString}` : pathname, {
      scroll: false
    });
  };
  const filterAndGrid = <div className="flex w-full gap-8">
      <FilterSidebar groups={[{
      kind: "select",
      items: sidebarCategories.map(c => ({
        key: c,
        label: c
      })),
      activeKey: activeCategory,
      onChange: handleCategoryChange
    }]} />

      <div className="flex min-w-0 flex-1 flex-col gap-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
          <Tabs value="all" className="min-w-0 flex-1">
            <TabsList className="w-full justify-start overflow-x-auto sm:w-auto">
              <TabsTrigger value="all" count={filteredApps.length}>
                All integrations
              </TabsTrigger>
              {x402Count > 0 && <TabsTrigger value="x402" count={x402Count} asChild>
                  <Link href="/integrations/x402">x402 Endpoints</Link>
                </TabsTrigger>}
            </TabsList>
          </Tabs>
          <div className="rounded-ds-md border-ds-border bg-ds-surface-sunken shadow-ds-edge-light-debossed relative flex h-[44px] w-full items-center gap-2 border py-[10px] pl-[10px] pr-2 sm:max-w-[400px] sm:shrink-0">
            <IconMagnifyingGlass className="text-ds-text-disabled h-5 w-5 shrink-0" />
            <Input type="text" placeholder="Search integrations..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="text-ds-text-primary placeholder:text-ds-text-disabled leading-label h-5 flex-1 border-0 bg-transparent p-0 text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0" />
          </div>
        </div>

        {isLoading ? <div className="flex items-center justify-center py-20">
            <PixelLoader size="xl" color="blue" />
          </div> : filteredApps.length === 0 ? <div className="flex flex-col items-center justify-center py-20 text-center">
            <p className="text-ds-text-muted text-base">
              {searchQuery ? "No integrations match your search" : activeCategory !== ALL_CATEGORIES ? `No integrations found in "${activeCategory}"` : "No integrations found"}
            </p>
            {(searchQuery || activeCategory !== ALL_CATEGORIES) && <button type="button" onClick={() => {
          setSearchQuery("");
          handleCategoryChange(ALL_CATEGORIES);
        }} className="text-ds-focus-brand mt-2 text-sm hover:underline">
                Clear filters
              </button>}
          </div> : <div className="grid grid-cols-2 gap-4 sm:grid-cols-[repeat(auto-fill,minmax(170px,1fr))] sm:gap-5 lg:grid-cols-[repeat(auto-fill,minmax(200px,1fr))]">
            {filteredApps.map(({
          app: app_2
        }, idx) => <motion.div key={app_2.slug} initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: Math.min(idx, 7) * 0.04,
          ease: EASE_OUT_QUART
        }}>
                <PublicAppCard app={app_2} />
              </motion.div>)}
          </div>}
      </div>
    </div>;
  if (isAuthenticated) {
    return <PageLayout>
        <div className="flex flex-col gap-8">
          <IntegrationHero flush />
          {filterAndGrid}
        </div>
      </PageLayout>;
  }
  return <div className="bg-ds-surface-primary relative min-h-screen">
      <div className="relative z-[2] px-4 2xl:px-0">
        <GridBorderedSection className="h-[72px]" />
        <GridDivider variant={2} />
        <GridBorderedSection>
          <IntegrationHero />
        </GridBorderedSection>
        <GridDivider variant={2} />

        <GridBorderedSection>
          <div className="relative z-[2] mx-auto w-full max-w-[1440px] pb-16 pt-2.5 sm:px-6 md:px-[41px]">
            {filterAndGrid}
          </div>
        </GridBorderedSection>
        <GridDivider variant={2} />
      </div>

      <HomepageFooter />
    </div>;
}
export default function IntegrationsPageClient(t0) {
  const $ = _c(3);
  const {
    initialApps,
    x402EndpointCount
  } = t0;
  let t1;
  if ($[0] !== initialApps || $[1] !== x402EndpointCount) {
    t1 = <Suspense><IntegrationsPageContent initialApps={initialApps} x402EndpointCount={x402EndpointCount} /></Suspense>;
    $[0] = initialApps;
    $[1] = x402EndpointCount;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}